import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, laneLabel } from "@/lib/format";
import {
  TASK_STATUSES, TASK_PRIORITIES, TASK_RELATED_TYPES, SERVICE_LANES,
  TONE, optOf, labelOf,
} from "@/config/taskOptions";
import QuickActions from "@/components/QuickActions";
import CommunicationsPanel from "@/components/CommunicationsPanel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PopoutDialog } from "@/components/ui/PopoutDialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ListChecks, Plus, Search, CircleDot, Loader2, AlertTriangle, CalendarClock,
  CheckCircle2, Pencil, Trash2, MessageSquare, Link2,
} from "lucide-react";
import { toast } from "sonner";

const Pill = ({ arr, value, testid }) => {
  if (!value) return <span className="text-xs text-muted-foreground">—</span>;
  const o = optOf(arr, value);
  const t = TONE[o?.tone || "gray"];
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border whitespace-nowrap"
      style={{ backgroundColor: t.bg, color: t.color, borderColor: t.border }} data-testid={testid}>
      {o?.label || labelOf(arr, value)}
    </span>
  );
};

const MetricCard = ({ icon: Icon, label, value, accent, testid, active, onClick }) => (
  <Card onClick={onClick}
    className={`p-3.5 border-[#E6EDF2] ${onClick ? "cursor-pointer transition-shadow hover:shadow-md" : ""} ${active ? "ring-2 ring-[#1C4358]/30" : ""}`}
    style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid={testid}>
    <div className="flex items-center gap-2.5">
      <span className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${accent}1A` }}>
        <Icon size={17} style={{ color: accent }} />
      </span>
      <div className="min-w-0">
        <div className="text-xl font-bold tabular-nums text-[#252221] leading-none">{value ?? 0}</div>
        <div className="text-[11px] text-muted-foreground mt-1 truncate">{label}</div>
      </div>
    </div>
  </Card>
);

const emptyForm = {
  title: "", description: "", assignee_id: "", due_date: "", priority: "normal",
  status: "not_started", related_type: "", related_id: "", related_service_lane: "", notes: "",
};

export default function Tasks() {
  const { user } = useAuth();
  const canWrite = !user?.is_portal;

  const [items, setItems] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("mine"); // mine | all
  const [filters, setFilters] = useState({ q: "", status: "all", priority: "all" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const loadMetrics = useCallback(() => {
    api.get("/tasks/metrics", { params: { mine: scope === "mine" } }).then((r) => setMetrics(r.data || {})).catch(() => {});
  }, [scope]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (scope === "mine") params.mine = true;
      if (filters.q) params.q = filters.q;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.priority !== "all") params.priority = filters.priority;
      const { data } = await api.get("/tasks", { params });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [scope, filters]);

  useEffect(() => { api.get("/users").then((r) => setUsers(r.data.items || r.data || [])).catch(() => setUsers([])); }, []);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { loadMetrics(); }, [loadMetrics]);

  const refresh = () => { load(); loadMetrics(); };

  const openNew = () => { setForm({ ...emptyForm, assignee_id: user?.id || "" }); setEditing(null); setOpen(true); };
  const openEdit = (t) => {
    setForm({
      ...emptyForm, ...t,
      assignee_id: t.assignee_id || "",
      related_type: t.related_type || "",
      related_service_lane: t.related_service_lane || "",
      status: t.status_norm || t.status || "not_started",
      priority: t.priority_norm || t.priority || "normal",
    });
    setEditing(t.id); setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Task title is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
      if (editing) await api.put(`/tasks/${editing}`, payload);
      else await api.post("/tasks", payload);
      toast.success(editing ? "Task updated" : "Task created");
      setOpen(false); refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save task"); } finally { setBusy(false); }
  };

  const setStatus = async (t, status) => {
    try { await api.put(`/tasks/${t.id}`, { status }); refresh(); } catch { toast.error("Could not update"); }
  };
  const toggleComplete = async (t) => {
    const done = t.status_norm === "completed";
    await setStatus(t, done ? "not_started" : "completed");
  };
  const remove = async (t) => {
    try { await api.delete(`/tasks/${t.id}`); toast.success("Task deleted"); refresh(); } catch { toast.error("Could not delete"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2"><ListChecks size={22} className="text-[#1C4358]" /> My Work</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Tasks, follow-ups, and communications that keep every family moving forward.</p>
        </div>
        {canWrite && (
          <div className="flex items-center gap-2">
            <QuickActions onCreated={refresh} />
            <Button onClick={openNew} data-testid="new-task-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}><Plus size={17} className="mr-1.5" /> New Task</Button>
          </div>
        )}
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5" data-testid="tasks-metrics">
        <MetricCard icon={CircleDot} label="Open" value={metrics.open} accent="#1C4358" testid="metric-open"
          active={filters.status === "open"} onClick={() => setFilters((p) => ({ ...p, status: p.status === "open" ? "all" : "open" }))} />
        <MetricCard icon={Loader2} label="In progress" value={metrics.in_progress} accent="#1C4358" testid="metric-in-progress"
          active={filters.status === "in_progress"} onClick={() => setFilters((p) => ({ ...p, status: p.status === "in_progress" ? "all" : "in_progress" }))} />
        <MetricCard icon={AlertTriangle} label="Overdue" value={metrics.overdue} accent="#9C1E22" testid="metric-overdue"
          active={filters.status === "overdue"} onClick={() => setFilters((p) => ({ ...p, status: p.status === "overdue" ? "all" : "overdue" }))} />
        <MetricCard icon={CalendarClock} label="Due today" value={metrics.due_today} accent="#9A6516" testid="metric-due-today" />
        <MetricCard icon={CheckCircle2} label="Completed" value={metrics.completed} accent="#256B4B" testid="metric-completed"
          active={filters.status === "completed"} onClick={() => setFilters((p) => ({ ...p, status: p.status === "completed" ? "all" : "completed" }))} />
      </div>

      <Tabs defaultValue="tasks">
        <TabsList>
          <TabsTrigger value="tasks" data-testid="tab-tasks"><ListChecks size={15} className="mr-1.5" /> Tasks</TabsTrigger>
          <TabsTrigger value="communications" data-testid="tab-communications"><MessageSquare size={15} className="mr-1.5" /> Communications</TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="mt-4">
          {/* Filters */}
          <Card className="p-3 mb-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
            <div className="flex flex-col lg:flex-row lg:items-center gap-2">
              <div className="inline-flex rounded-lg border border-[#E6EDF2] p-0.5 bg-[#F7FAFC]" data-testid="scope-toggle">
                {["mine", "all"].map((s) => (
                  <button key={s} onClick={() => setScope(s)} data-testid={`scope-${s}`}
                    className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${scope === s ? "bg-white text-[#1C4358] shadow-sm" : "text-muted-foreground hover:text-[#1C4358]"}`}>
                    {s === "mine" ? "My tasks" : "All tasks"}
                  </button>
                ))}
              </div>
              <div className="relative flex-1 min-w-[180px]">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={filters.q} onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))} placeholder="Search tasks..." className="pl-9 h-9" data-testid="task-search" />
              </div>
              <Select value={filters.status} onValueChange={(v) => setFilters((p) => ({ ...p, status: v }))}>
                <SelectTrigger className="h-9 w-full sm:w-[170px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  {TASK_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.priority} onValueChange={(v) => setFilters((p) => ({ ...p, priority: v }))}>
                <SelectTrigger className="h-9 w-full sm:w-[150px]" data-testid="filter-priority"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Any priority</SelectItem>{TASK_PRIORITIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </Card>

          {/* Table */}
          <div className="rounded-xl border border-[#E6EDF2] bg-white overflow-hidden" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
            {loading ? (
              <div className="p-4 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center">
                <div className="mx-auto mb-3 h-14 w-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "rgba(28,67,88,0.08)" }}><ListChecks size={26} className="text-[#1C4358]" /></div>
                <p className="font-medium text-[#252221]">No tasks here</p>
                <p className="text-sm text-muted-foreground">{scope === "mine" ? "You're all caught up. Add a task to plan your next step." : "Create a task to get started."}</p>
              </div>
            ) : (
              <div className="overflow-x-auto mmm-scroll">
                <Table data-testid="tasks-table">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-10"></TableHead>
                      <TableHead className="text-xs font-semibold text-[#1C4358]/80">Task</TableHead>
                      <TableHead className="text-xs font-semibold text-[#1C4358]/80">Assignee</TableHead>
                      <TableHead className="text-xs font-semibold text-[#1C4358]/80">Due</TableHead>
                      <TableHead className="text-xs font-semibold text-[#1C4358]/80">Priority</TableHead>
                      <TableHead className="text-xs font-semibold text-[#1C4358]/80">Status</TableHead>
                      <TableHead className="text-right text-xs font-semibold text-[#1C4358]/80">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((t) => {
                      const done = t.status_norm === "completed";
                      return (
                        <TableRow key={t.id} className="hover:bg-[rgba(155,185,206,0.10)]" data-testid="task-row">
                          <TableCell className="py-3">
                            <Checkbox checked={done} onCheckedChange={() => canWrite && toggleComplete(t)} disabled={!canWrite} data-testid="task-complete-checkbox" />
                          </TableCell>
                          <TableCell className="py-3">
                            <div className={`font-semibold ${done ? "line-through text-muted-foreground" : "text-[#252221]"}`}>{t.title}</div>
                            <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
                              {t.related_type && <span className="inline-flex items-center gap-1"><Link2 size={11} /> {labelOf(TASK_RELATED_TYPES, t.related_type)}</span>}
                              {t.related_service_lane && <span>· {laneLabel(t.related_service_lane)}</span>}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{t.assignee_name || "—"}</TableCell>
                          <TableCell className="text-sm">
                            {t.due_date ? (
                              <span className={t.is_overdue ? "text-[#9C1E22] font-semibold" : "text-muted-foreground"}>{formatDate(t.due_date)}</span>
                            ) : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell><Pill arr={TASK_PRIORITIES} value={t.priority_norm} testid="task-priority-pill" /></TableCell>
                          <TableCell>
                            {canWrite ? (
                              <Select value={t.status_norm} onValueChange={(v) => setStatus(t, v)}>
                                <SelectTrigger className="h-8 w-[170px]" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{TASK_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                              </Select>
                            ) : <Pill arr={TASK_STATUSES} value={t.status_norm} />}
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            {canWrite && (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid="edit-task-button"><Pencil size={14} /></Button>
                                <Button size="sm" variant="ghost" onClick={() => remove(t)} data-testid="delete-task-button"><Trash2 size={14} className="text-[#9C1E22]" /></Button>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-2">{items.length} task{items.length === 1 ? "" : "s"}</p>
        </TabsContent>

        <TabsContent value="communications" className="mt-4">
          <Card className="p-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
            <CommunicationsPanel />
          </Card>
        </TabsContent>
      </Tabs>

      {/* New / Edit task dialog — modal by default, detachable to a floating pop-out */}
      <PopoutDialog
        open={open}
        onOpenChange={setOpen}
        title={editing ? "Edit Task" : "New Task"}
        subtitle="My Work · Tasks"
        icon={ListChecks}
        maxWidthClass="max-w-xl"
        testid="task-dialog"
      >
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm text-muted-foreground -mt-1">Plan a clear next step and assign it to the right teammate.</p>
            <div><Label className="mb-1.5 block text-[13px]">Title *</Label><Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} data-testid="task-title-input" /></div>
            <div><Label className="mb-1.5 block text-[13px]">Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} data-testid="task-description-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-[13px]">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm((p) => ({ ...p, priority: v }))}>
                  <SelectTrigger data-testid="task-priority-input"><SelectValue /></SelectTrigger>
                  <SelectContent>{TASK_PRIORITIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}>
                  <SelectTrigger data-testid="task-status-input"><SelectValue /></SelectTrigger>
                  <SelectContent>{TASK_STATUSES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm((p) => ({ ...p, due_date: e.target.value }))} data-testid="task-due-input" /></div>
              <div><Label className="mb-1.5 block text-[13px]">Assignee</Label>
                <Select value={form.assignee_id || user?.id} onValueChange={(v) => setForm((p) => ({ ...p, assignee_id: v }))}>
                  <SelectTrigger data-testid="task-assignee-input"><SelectValue placeholder="Assign to..." /></SelectTrigger>
                  <SelectContent className="max-h-[260px]">
                    {users.length === 0 && <SelectItem value={user?.id}>{user?.full_name || "Me"}</SelectItem>}
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Related to</Label>
                <Select value={form.related_type || "none"} onValueChange={(v) => setForm((p) => ({ ...p, related_type: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="task-related-input"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{TASK_RELATED_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-[13px]">Service lane</Label>
                <Select value={form.related_service_lane || "none"} onValueChange={(v) => setForm((p) => ({ ...p, related_service_lane: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="task-lane-input"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{SERVICE_LANES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="mb-1.5 block text-[13px]">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} data-testid="task-notes-input" /></div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="save-task-button">{busy ? "Saving..." : editing ? "Save changes" : "Create task"}</Button>
            </div>
        </form>
      </PopoutDialog>
    </div>
  );
}
